/*eslint-disable*/
import moment from 'moment';
import storeServices from '@/Services/Form Services/store.services';
import processFlowServices from '@/Services/Analytics Services/process-flow.services';
import shiftServices from '@/Services/shift.service';
import allVendorServices from '@/Services/Form Services/allvendor.services';
import inventoryAnalyticsServices from '@/Services/Analytics Services/inventory-analytics.services';
import { getHigherUnitAndQuantity, unitMap } from '@/Helpers';

const inHigherUnit = (qty, unit) => {
	const resolved = unit != null && unit !== '' && !Number.isNaN(Number(unit))
		? unitMap[Number(unit)]
		: unit;
	const [higherQty, higherUnit] = getHigherUnitAndQuantity(
		resolved,
		Number(qty) || 0
	);
	return {
		qty: higherQty,
		unit: higherUnit,
		baseQty: qty,
		baseUnit: resolved,
	};
};

export const flowDate = (state) => state?.planDate ?? state?.expectedDeliveryDate;

export const PLANNABLE_ORDER_STATUSES = [
	'PENDING',
	'DISPATCH',
	'TRANSIT',
	'UNLOADING',
	'VERIFICATION',
	'FULFILLED',
	'APPROVAL_PENDING',
];

const isCancelledPo = (po) =>
	String(po?.orderStatus || '').toUpperCase() === 'CANCELLED';

const hasProductionPlan = (po) => !!po?.jsonData?.generatedPPId;

const isInternalPo = (po, state) => {
	if (po?.subType) return false;
	const to = po?.storeId ?? po?.store;
	if (to == null) return false;
	const from = po?.fromStoreId ?? state?.fromStore;
	return String(to) !== String(from);
};

const buyerColumnId = (vendorId, locationId) => `${vendorId}_${locationId}`;

const buyerLocationLabel = (lists, vendorId, locationId) => {
	const buyer = (lists?.buyerList || []).find(
		(b) => `${b?.value}` === `${vendorId}`
	);
	const location = (buyer?.children || []).find(
		(l) => `${l?.value}` === `${locationId}`
	);
	return {
		name: location?.label,
		groupName: buyer?.label || buyer?.name,
		clientBuyerId: buyer?.clientVendorId ?? null,
	};
};

const buyerColumns = {
	idOf: (po) => buyerColumnId(po?.vendorId, po?.vendorLocationId),
	columnOf: (po, { lists }) => {
		const label = buyerLocationLabel(lists, po?.vendorId, po?.vendorLocationId);
		return {
			id: buyerColumnId(po?.vendorId, po?.vendorLocationId),
			name: label.name || `Location ${po?.vendorLocationId}`,
			groupName: label.groupName || po?.vendorName || 'Buyer',
			clientBuyerId: label.clientBuyerId,
		};
	},
};

const internalColumns = {
	idOf: (po) => `${po?.storeId ?? po?.store}`,
	columnOf: (po, { lists }) => {
		const id = po?.storeId ?? po?.store;
		const store = (lists?.storeList || []).find(
			(s) => `${s?.value}` === `${id}`
		);
		return {
			id: `${id}`,
			name: po?.storeName || store?.label || `Store ${id}`,
			shortName: store?.shortName ?? null,
		};
	},
};

const buildTable = (pos, strategy, ctx) => {
	const { productList } = ctx || {};
	const columnMap = new Map();
	pos.forEach((po) => {
		const id = strategy.idOf(po);
		if (id != null && !columnMap.has(id)) {
			columnMap.set(id, strategy.columnOf(po, ctx || {}));
		}
	});
	const columns = [...columnMap.values()]
		.sort((a, b) => String(a?.name).localeCompare(String(b?.name)));

	const grouped = {};
	const sources = {};
	pos.forEach((po) => {
		const columnId = strategy.idOf(po);
		(po?.items || []).forEach((item) => {
			const key = `${item?.variantId}|${columnId}`;
			if (!sources[key]) sources[key] = [];
			sources[key].push({
				poId: po?.id,
				poItemId: item?.poItemId ?? item?.id,
				qty: Number(item?.qty) || 0,
			});
			if (!grouped[item?.variantId]) {
				grouped[item?.variantId] = {
					productName: item?.productName,
					variantName: item?.variantName,
					variantId: item?.variantId,
					productId: item?.productId,
					unit: item?.unit,
					clientVariantId: item?.clientVariantId,
					qtyByColumn: {},
				};
			}
			grouped[item.variantId].qtyByColumn[columnId] =
				(grouped[item.variantId].qtyByColumn[columnId] || 0)
				+ (Number(item?.qty) || 0);
		});
	});

	const rows = Object.values(grouped).map((item, index) => ({
		'S.No': index + 1,
		'Item Name': `${item?.productName}(${item?.variantName})`,
		itemId: item?.variantId,
		SkuCode:
			productList
				?.flatMap((product) => product?.variants || [])
				?.find((variant) => variant?.id == item?.variantId)
				?.clientVariantId ?? item?.clientVariantId,
		Total: '',
		...columns.reduce((acc, column) => {
			const qty = item.qtyByColumn[column.id];
			const converted = inHigherUnit(qty, item?.unit);
			acc[`${column.id}`] = {
				...converted,
				qty: qty != null ? converted.qty : '',
				storeId: column.id,
				itemId: item?.variantId,
				productId: item?.productId,
				sources: sources[`${item?.variantId}|${column.id}`] || [],
			};
			return acc;
		}, {}),
	}));

	return {
		headers: [
			{ name: 'S.No', id: 'S.No' },
			{ name: 'Item Name', id: 'Item Name' },
			{ name: 'SkuCode', id: 'SkuCode' },
			...columns.map((column) => ({
				name: column.name,
				storeName: column.name,
				shortName: column.shortName,
				groupName: column.groupName,
				clientBuyerId: column.clientBuyerId,
				id: column.id,
			})),
			{ name: 'Total', id: 'Total' },
		],
		rows,
		stores: columns,
		orderIds: pos.map((po) => po?.id).filter((id) => id != null),
	};
};

export const apiFetchers = {
	fromStore: {
		listKey: 'storeList',
		apiFunction: storeServices?.getStore,
		requestBuilder: () => ({
			id: '',
			type: 'BASE,WAREHOUSE_PRODUCT,WAREHOUSE,WAREHOUSE_INGREDIENT,RETAIL',
		}),
		transform: (response) => {
			const storeList = response?.data?.map((store) => ({
				label: store.name,
				value: store.id,
				type: store.type,
				...store,
			})) || [];
			return storeList.sort((a, b) => {
				if (a.type === 'BASE' && b.type !== 'BASE') return -1;
				if (a.type !== 'BASE' && b.type === 'BASE') return 1;
				return 0;
			});
		},
	},
	internalProcess: {
		listKey: 'processList',
		dependsOn: ['fromStore'],
		apiFunction: processFlowServices?.fetchNodes,
		requestBuilder: ({ state }) => ({
			params: {
				storeId: state?.fromStore?.id || state?.fromStore,
			},
		}),
		transform: (response) => response?.data?.nodes?.map((process) => ({
			label: `${process?.data?.heading} (Section: ${process?.data?.sectionName ?? ''})`,
			value: process.id,
			...process,
		})) || [],
	},
	internalShift: {
		listKey: 'shiftList',
		dependsOn: ['internalProcess'],
		apiFunction: shiftServices.listProcessShifts,
		requestBuilder: ({ state }) => ({
			data: {
				date: moment(new Date()).format('YYYY-MM-DD'),
				processIds: [state?.internalProcess?.id || state?.internalProcess],
				store: state?.fromStore?.id || state?.fromStore,
			},
		}),
		transform: (response) => response?.data?.map((shift) => ({
			label: shift.shiftName,
			value: shift.id,
			...shift,
		})) || [],
	},
	buyer: {
		listKey: 'buyerList',
		apiFunction: allVendorServices?.listVendor,
		requestBuilder: () => ({
			type: 'BUYER',
		}),
		transform: (response) => response?.data?.map((buyer) => ({
			label: buyer.name,
			value: buyer.id,
			children: buyer?.vendor_locations?.map((location) => ({
				label: location.name,
				value: location.id,
				...location,
			})) || [],
			...buyer,
		})) || [],
	},
	orderTables: {
		apiFunction: async ({ buyerReq, internalReq }) => {
			const [buyerRes, internalRes] = await Promise.all([
				inventoryAnalyticsServices.fetchPoList(buyerReq),
				inventoryAnalyticsServices.fetchPoList(internalReq),
			]);
			if (buyerRes?.statusCode !== 200 && internalRes?.statusCode !== 200) {
				return buyerRes;
			}
			return {
				statusCode: 200,
				data: {
					buyerPos: buyerRes?.statusCode === 200 ? (buyerRes?.data || []) : [],
					internalPos: internalRes?.statusCode === 200 ? (internalRes?.data || []) : [],
				},
			};
		},
		requestBuilder: ({ state }) => {
			const from = moment().format('YYYY-MM-DD');
			const to = moment().add(2, 'days').format('YYYY-MM-DD');
			return {
				buyerReq: {
					req: {
						data: {
							filters: {
								fromStoreId: state?.fromStore ?? null,
								status:PLANNABLE_ORDER_STATUSES,
								buyers: [],
								variantIds: [],
								orderType: 'buyers',
								userOrderType: 'sales',
								dateRangeFilter: { dateType: 'ordered', from, to },
							},
							showInternalData: false,
						},
					},
				},
				internalReq: {
					req: {
						data: {
							filters: {
								fromStoreId: state?.fromStore ?? null,
								status: PLANNABLE_ORDER_STATUSES,
								buyers: [],
								variantIds: [],
								orderType: 'sales/purchase',
								userOrderType: 'sales',
								dateRangeFilter: { dateType: '', from, to },
							},
							showInternalData: false,
						},
					},
				},
			};
		},
		transform: (response, ctx) => {
			const clean = (pos) => (pos || []).filter(
				(po) => !isCancelledPo(po) && !hasProductionPlan(po)
			);
			return {
				buyerOrders: buildTable(clean(response?.data?.buyerPos), buyerColumns, ctx),
				internalSales: buildTable(
					clean(response?.data?.internalPos)
						.filter((po) => isInternalPo(po, ctx?.state)),
					internalColumns,
					ctx
				),
			};
		},
	},
	tableHeaderStores: {
		apiFunction: storeServices?.getStore,
		requestBuilder: () => ({
			id: '',
			type: 'RETAIL',
		}),
		transform: (response, { state }) => {
			const storeList = response?.data?.filter(
				(store) => store?.parentStore === state?.fromStore
			);
			return {
				headers: [
					{ name: 'S.No', id: 'S.No' },
					{ name: 'Item Name', id: 'Item Name' },
					{ name: 'SkuCode', id: 'SkuCode' },
					...[...storeList]
						?.slice()
						.sort(
							(a, b) =>
								(a.priority ?? 9999) - (b.priority ?? 9999)
						)
						.map((store) => ({
							name: `${store?.name} ${store?.shortName ? `(${store?.shortName})` : ''}`,
							storeName: store?.name,
							shortName: store?.shortName,
							priority: store?.priority,
							id: store?.id,
						})),
					{ name: 'Total', id: 'Total' },
					{ name: 'Actions', id: 'Actions' },
				],
				apiStoreList: storeList,
			};
		},
	},
	tableRowsItems: {
		apiFunction: inventoryAnalyticsServices?.fetchStandardPO,
		requestBuilder: ({ state, lists }) => ({
			params: {
				fromStore: state?.fromStore ?? null,
				orderType: state?.orderType ?? 'CMS',
				shiftName:
					(lists?.shiftList ?? [])?.find(
						(item) => item?.id === state?.internalShift
					)?.shiftName ?? null,
				orderDate: moment(flowDate(state)).format('DD/MM/YYYY'),
			},
		}),
		transform: (response, { productList, stores }) =>
			response?.data?.map((item, index) => ({
				Actions: { rowIndex: index },
				'S.No': index + 1,
				'Item Name': item?.productName + '(' + item?.variantName + ')',
				itemId: item?.variantId,
				SkuCode:
					productList
						?.flatMap((product) => product?.variants || [])
						?.find((variant) => variant?.id == item?.variantId)
						?.clientVariantId ?? item?.clientVariantId,
				Total: '',
				...stores?.reduce((acc, store) => {
					acc[`${store?.id}`] = {
						qty: '',
						unit: inHigherUnit(0, item?.unit).unit,
						storeId: store?.id,
						itemId: item?.variantId,
						productId: item?.productId,
					};
					return acc;
				}, {}),
			})) || [],
	},
};
export const apiUpdaters = {
	orderItems: {
		apiFunction: inventoryAnalyticsServices?.editVendorPO,
		requestBuilder: ({ poId, items }) => ({
			params: { poId },
			data: { items },
		}),
	},
};

export const runApiUpdate = async (key, payload = {}) => {
	const entry = apiUpdaters?.[key];
	if (!entry?.apiFunction) return null;
	const request = entry.requestBuilder
		? entry.requestBuilder(payload)
		: payload;
	return entry.apiFunction(request);
};

export const runApiFetch = async (key, ctx = {}) => {
	const entry = apiFetchers?.[key];
	if (!entry?.apiFunction) return null;
	if (
		entry.dependsOn &&
		!entry.dependsOn.every((dep) => ctx?.state?.[dep])
	) {
		return null;
	}
	const request = entry.requestBuilder
		? entry.requestBuilder(ctx)
		: undefined;
	const response = await entry.apiFunction(request);
	if (response?.statusCode !== 200) return null;
	return entry.transform ? entry.transform(response, ctx) : response?.data;
};

export const tableBuilders = {
	buyerStatic: ({ state, lists, prevRows = [] }) => {
		const buyerList = lists?.buyerList || [];
		const pickedLeaves = state?.drillDown?.buyerSelection || [];
		const clientIdOfLeaf = (leaf) => {
			const parent = buyerList.find((b) => `${b?.value}` === `${leaf?.parentId}`)
				|| buyerList.find((b) =>
					(b?.children || []).some((l) => `${l?.value}` === `${leaf?.id}`)
				);
			return parent?.clientVendorId ?? null;
		};
		const buyerColumns = pickedLeaves.length
			? pickedLeaves.map((leaf) => ({
				label: leaf?.name,
				value: leaf?.id,
				groupName: leaf?.parentName || 'Buyer',
				clientBuyerId: clientIdOfLeaf(leaf),
			}))
			: [];
		if (!buyerColumns.length) {
			const selected = Array.isArray(state?.buyer) ? state.buyer : [];
			buyerList.forEach((buyer) => {
				(buyer?.children || []).forEach((location) => {
					if (
						selected.includes(buyer.value) ||
						selected.includes(location.value)
					) {
						buyerColumns.push({
							...location,
							groupName: buyer?.label || buyer?.name,
							clientBuyerId: buyer?.clientVendorId ?? null,
						});
					}
				});
			});
		}
		return {
			headers: [
				{ name: 'S.No', id: 'S.No' },
				{ name: 'Item Name', id: 'Item Name' },
				{ name: 'SkuCode', id: 'SkuCode' },
				...buyerColumns.map((location) => ({
					name: location.label,
					id: location.value,
					groupName: location.groupName,
					clientBuyerId: location.clientBuyerId,
				})),
				{ name: 'Total', id: 'Total' },
				{ name: 'Actions', id: 'Actions' },
			],
			rows: prevRows,
		};
	},
};
